import { useState } from "react";

import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Table from "react-bootstrap/Table";
import { FaEllipsisV, FaInfoCircle } from "react-icons/fa";

import { handleColumnToggle } from "@/utils/handleColumnTogle";
import {
	getIndicatorClassification,
	getIndicatorClassificationColorClassName,
	getIndicatorClassificationScores,
	IndicatorEsfEnum,
} from "@/utils/indicatorsEsf";
import { calculatePercentage } from "@/utils/calculatePercentage";
import { ResumeIndicatorC5ByHealthTeamData } from "@/services/esus/indicator/esf/c5/getResumeIndicatorC5ByHealthTeam";
import { Column } from "@/@types/generics/Column";

import { TableSkeletons } from "@/components/TableSkeletons";
import { CardOverlayTrigger } from "@/components/CardOverlayTrigger";

type IndicatorEsfC5HealthTeamsClassificationTableColumns = {
	healthTeamName: Column;
	totalHypertensiveCitizensInPeriod: Column;
	totalScore: Column;
	classificationPercentage: Column;
	classification: Column;
};

type IndicatorEsfC5HealthTeamsTableProps = {
	healthTeamsClassifications: ResumeIndicatorC5ByHealthTeamData[];
	isLoading: boolean;
};

const indicatorClassificationScores = getIndicatorClassificationScores(IndicatorEsfEnum.C5);

export function IndicatorEsfC5HealthTeamsClassificationTable({
	healthTeamsClassifications,
	isLoading,
}: Readonly<IndicatorEsfC5HealthTeamsTableProps>) {
	const [columns, setColumns] = useState<IndicatorEsfC5HealthTeamsClassificationTableColumns>({
		healthTeamName: { title: "Equipe", visible: true },
		totalHypertensiveCitizensInPeriod: { title: "Total de hipertensos", visible: true },
		totalScore: { title: "Pontuação", visible: true },
		classificationPercentage: { title: "% Classificação", visible: true },
		classification: { title: "Classificação", visible: true },
	});

	return (
		<Table striped bordered hover responsive>
			<thead className="text-center fs-7">
				<tr className="fw-bolder text-center">
					<th
						className={`text-start px-2 ${!columns.healthTeamName.visible && "d-none"}`}
					>
						{columns.healthTeamName.title}
					</th>
					<th
						className={`px-2 ${
							!columns.totalHypertensiveCitizensInPeriod.visible && "d-none"
						}`}
					>
						{columns.totalHypertensiveCitizensInPeriod.title}
					</th>
					<th className={`px-2 ${!columns.totalScore.visible && "d-none"}`}>
						{columns.totalScore.title}
					</th>
					<th className={`px-2 ${!columns.classificationPercentage.visible && "d-none"}`}>
						{columns.classificationPercentage.title}
					</th>
					<th className={`px-2 ${!columns.classification.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.classification.title}{" "}
							<CardOverlayTrigger
								header={"Classificação"}
								icon={<FaInfoCircle className="text-muted" />}
							>
								<div className="text-start">
									<div className="text-primary">{`Ótimo: >= ${indicatorClassificationScores.awesome}% e <= ${indicatorClassificationScores.limit}%`}</div>
									<br />
									<div className="text-success">{`Bom: >= ${indicatorClassificationScores.good}% e < ${indicatorClassificationScores.awesome}%`}</div>
									<br />
									<div className="text-warning">
										{`Suficiente: >= ${indicatorClassificationScores.enough}% e < ${indicatorClassificationScores.good}%`}
									</div>
									<br />
									<div className="text-danger">{`Regular: < ${indicatorClassificationScores.enough}%`}</div>
									<br />
								</div>
							</CardOverlayTrigger>
						</div>
					</th>
					<th className="text-nowrap pb-0">
						<OverlayTrigger
							trigger="click"
							placement="bottom"
							overlay={
								<Popover>
									<Popover.Body>
										<Form className="d-flex flex-column gap-2">
											{Object.keys(columns).map((key, index) => (
												<Form.Check
													key={`citizen-attendances-table-column-${
														index + 1
													}`}
													type="checkbox"
													label={
														columns[
															key as keyof IndicatorEsfC5HealthTeamsClassificationTableColumns
														].title
													}
													checked={
														columns[
															key as keyof IndicatorEsfC5HealthTeamsClassificationTableColumns
														].visible
													}
													onChange={() =>
														handleColumnToggle(
															key as keyof IndicatorEsfC5HealthTeamsClassificationTableColumns,
															setColumns
														)
													}
												/>
											))}
										</Form>
									</Popover.Body>
								</Popover>
							}
							rootClose
						>
							<Button
								title="Exibição de colunas da tabela"
								variant="white"
								className="px-2"
								disabled={isLoading}
							>
								<FaEllipsisV />
							</Button>
						</OverlayTrigger>
					</th>
				</tr>
			</thead>
			<tbody className="fs-8">
				{!isLoading && healthTeamsClassifications.length > 0
					? healthTeamsClassifications.map((healthTeamClassification, index) => (
							<tr
								className="text-center"
								key={`health-team-classification-table-row-${index + 1}`}
							>
								<td
									className={`text-start px-2 ${
										!columns.healthTeamName.visible && "d-none"
									}`}
								>
									<div className="d-flex flex-column">
										<div>{healthTeamClassification.healthTeam?.name ?? ""}</div>
										<div className="text-muted">
											{healthTeamClassification.healthTeam?.ine ?? ""}
										</div>
									</div>
								</td>
								<td
									className={`px-2 ${
										!columns.totalHypertensiveCitizensInPeriod.visible &&
										"d-none"
									}`}
								>
									{healthTeamClassification.totalHypertensiveCitizensInPeriod ??
										0}
								</td>
								<td className={`px-2 ${!columns.totalScore.visible && "d-none"}`}>
									{healthTeamClassification.totalHypertensiveCitizensInPeriodScore ??
										0}
								</td>
								<td
									className={`px-2 ${
										!columns.classificationPercentage.visible && "d-none"
									}`}
								>
									{`${calculatePercentage(
										healthTeamClassification.totalHypertensiveCitizensInPeriodScore,
										healthTeamClassification.maxHypertensiveCitizensInPeriodScore
									)}%`}
								</td>
								<td
									className={`text-nowrap px-2 ${
										!columns.classification.visible && "d-none"
									}`}
								>
									<Badge
										bg={getIndicatorClassificationColorClassName(
											IndicatorEsfEnum.C5,
											calculatePercentage(
												healthTeamClassification.totalHypertensiveCitizensInPeriodScore,
												healthTeamClassification.maxHypertensiveCitizensInPeriodScore
											)
										)}
									>
										{getIndicatorClassification(
											IndicatorEsfEnum.C5,
											calculatePercentage(
												healthTeamClassification.totalHypertensiveCitizensInPeriodScore,
												healthTeamClassification.maxHypertensiveCitizensInPeriodScore
											)
										)}
									</Badge>
								</td>
								<td></td>
							</tr>
					  ))
					: isLoading && (
							<TableSkeletons
								numberOfCells={
									Object.values(columns).reduce(
										(count, column) =>
											count + (column?.visible === true ? 1 : 0),
										0
									) + 1
								}
							/>
					  )}
			</tbody>
		</Table>
	);
}
